import type {ReactElement} from 'react';
import React from 'react';
import {ControlledList} from '@Components/controlled-list';
import {getWidthAndHeightInPixels, PosterTypeUnits} from '@Libraries/poster-object.library';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {BaseCustomSizeOption} from '@Components/download-panel/components/base-custom-size-option/base-custom-size-option';
import {SizeDropdownOptions} from '@Components/download-panel/components/download-design-size-option-wrapper/download-design-size-option-wrapper.types';
import type {PosterLoadObject} from '@PosterWhiteboard/poster/poster.types';
import {isUserPAYG} from '@Libraries/user.library';
import {MAX_FREEBIE_DIMENSION} from '@Libraries/poster-download-library';

interface GifDesignSizePopupProps {
  getSliderForGIF: () => ReactElement;
  onClickListItem: (id: SizeDropdownOptions) => void;
  doShowOriginalValue: boolean;
  scale: number;
  posterObject: PosterLoadObject;
  selectedDisplaySizeOption: SizeDropdownOptions;
  setSelectedDisplaySizeOption: (newVal: SizeDropdownOptions) => void;
}

export function GifDesignSizePopup({
  getSliderForGIF,
  onClickListItem,
  doShowOriginalValue,
  scale,
  posterObject,
  selectedDisplaySizeOption,
  setSelectedDisplaySizeOption,
}: GifDesignSizePopupProps): ReactElement {
  const getUnitsForDisplay = (): PosterTypeUnits => {
    return PosterTypeUnits.PX;
  };

  const getLargeWidthAndHeight = (): {width: number; height: number} => {
    const {width: convertedWidth, height: convertedHeight} = getWidthAndHeightInPixels(posterObject);
    const maxDimension = Math.max(convertedWidth, convertedHeight);
    const threshold = 1380;

    const scaleFactor = threshold / maxDimension;
    return {
      width: Math.round(convertedWidth * scaleFactor),
      height: Math.round(convertedHeight * scaleFactor),
    };
  };

  const getSmallWidthAndHeight = (): {width: number; height: number} => {
    const {width: convertedWidth, height: convertedHeight} = getWidthAndHeightInPixels(posterObject);
    const minDimension = Math.min(convertedWidth, convertedHeight);
    const threshold = 540;

    const scaleFactor = threshold / minDimension;
    return {
      width: Math.round(convertedWidth * scaleFactor),
      height: Math.round(convertedHeight * scaleFactor),
    };
  };

  const getOriginalCompressedWidthAndHeight = (): {width: number; height: number} => {
    const {width: pixelWidth, height: pixelHeight} = getWidthAndHeightInPixels(posterObject);
    const maxDimension = Math.max(pixelWidth, pixelHeight);

    if (maxDimension <= MAX_FREEBIE_DIMENSION) {
      return {width: pixelWidth, height: pixelHeight};
    }

    const scaleFactor = MAX_FREEBIE_DIMENSION / maxDimension;
    return {
      width: Math.round(pixelWidth * scaleFactor),
      height: Math.round(pixelHeight * scaleFactor),
    };
  };

  const getTextForEachOption = (id: SizeDropdownOptions): string => {
    switch (id) {
      case SizeDropdownOptions.ORIGINAL:
        return `${window.i18next.t('pmwjs_original')} · ${getWidthAndHeightInPixels(posterObject).width} x ${getWidthAndHeightInPixels(posterObject).height}${getUnitsForDisplay()}`;
      case SizeDropdownOptions.ORIGINAL_COMPRESSED: {
        const compressed = getOriginalCompressedWidthAndHeight();
        return `${window.i18next.t('pmwjs_original_compressed')} · ${compressed.width} x ${compressed.height}${getUnitsForDisplay()}`;
      }
      case SizeDropdownOptions.SMALL:
        return `${window.i18next.t('pmwjs_small')} · ${getSmallWidthAndHeight().width} x ${getSmallWidthAndHeight().height}${getUnitsForDisplay()}`;
      case SizeDropdownOptions.LARGE:
        return `${window.i18next.t('pmwjs_large')} · ${getLargeWidthAndHeight().width} x ${getLargeWidthAndHeight().height}${getUnitsForDisplay()}`;
      default:
        return '';
    }
  };

  const isCustomOptionSelected = (): boolean => {
    return selectedDisplaySizeOption === SizeDropdownOptions.CUSTOM;
  };

  const getSizeForOption = (id: SizeDropdownOptions): number => {
    switch (id) {
      case SizeDropdownOptions.SMALL: {
        const small = getSmallWidthAndHeight();
        return small.width * small.height;
      }
      case SizeDropdownOptions.ORIGINAL: {
        const original = getWidthAndHeightInPixels(posterObject);
        return original.width * original.height;
      }
      case SizeDropdownOptions.ORIGINAL_COMPRESSED: {
        const compressed = getOriginalCompressedWidthAndHeight();
        return compressed.width * compressed.height;
      }
      case SizeDropdownOptions.LARGE: {
        const large = getLargeWidthAndHeight();
        return large.width * large.height;
      }
      default:
        return 0;
    }
  };

  const getItemsForPopup = (): ControlledListItemType[] => {
    const items: ControlledListItemType[] = [];

    if (isUserPAYG()) {
      const {width, height} = getWidthAndHeightInPixels(posterObject);
      const maxDimension = Math.max(width, height);

      if (maxDimension > MAX_FREEBIE_DIMENSION) {
        items.push({
          id: SizeDropdownOptions.ORIGINAL_COMPRESSED,
          type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
          text: getTextForEachOption(SizeDropdownOptions.ORIGINAL_COMPRESSED),
          isSelected: selectedDisplaySizeOption === SizeDropdownOptions.ORIGINAL_COMPRESSED,
        });
      }
    }

    if (doShowOriginalValue) {
      items.push({
        id: SizeDropdownOptions.ORIGINAL,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: getTextForEachOption(SizeDropdownOptions.ORIGINAL),
        isSelected: selectedDisplaySizeOption === SizeDropdownOptions.ORIGINAL,
      });
    }

    items.push(
      {
        id: SizeDropdownOptions.SMALL,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: getTextForEachOption(SizeDropdownOptions.SMALL),
        isSelected: selectedDisplaySizeOption === SizeDropdownOptions.SMALL,
      },
      {
        id: SizeDropdownOptions.LARGE,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: getTextForEachOption(SizeDropdownOptions.LARGE),
        isSelected: selectedDisplaySizeOption === SizeDropdownOptions.LARGE,
      }
    );

    return items.sort((a, b) => {
      const sizeA = getSizeForOption(a.id as SizeDropdownOptions);
      const sizeB = getSizeForOption(b.id as SizeDropdownOptions);
      return sizeA - sizeB;
    });
  };

  const getCustomOptionText = (): string => {
    return isCustomOptionSelected()
      ? ` ${window.i18next.t('pmwjs_custom')} · ${Math.round(getWidthAndHeightInPixels(posterObject).width * scale)} x ${Math.round(getWidthAndHeightInPixels(posterObject).height * scale)}${getUnitsForDisplay()}`
      : window.i18next.t('pmwjs_custom');
  };

  return (
    <div className="flex flex-v-row">
      <ControlledList onClick={onClickListItem} items={getItemsForPopup()} className="spacing-m-0" />
      <BaseCustomSizeOption
        isSelected={isCustomOptionSelected()}
        customText={getCustomOptionText()}
        onClick={(e): void => {
          setSelectedDisplaySizeOption(SizeDropdownOptions.CUSTOM);
          e.stopPropagation();
        }}
        sliderElement={getSliderForGIF()}
      />
    </div>
  );
}
